// macadamia-app/src/macadamia-core/components/widgets/WidgetRenderer.tsx
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { IBlock, ITitleBlock, ICompositeTextWidget, IImageBlock, IGifBlock, ISuggestion, IListBlock, IVideoBlock, ITableBlock, IParagraphWidget, ISummaryBlock, ISeparatorBlock, IDossier } from './types';
import TitleWidget from './TitleWidget';
import CompositeTextWidget from './composite-text-widget/CompositeTextWidget';
import ParagraphWidget from './ParagraphWidget';
import ListWidget from './ListWidget';
import ImageWidget from './ImageWidget';
import GifWidget from './GifWidget';
import VideoWidget from './video/VideoWidget';
import TableWidget from './TableWidget';
import SummaryWidget from './SummaryWidget';
import SeparatorWidget from './SeparatorWidget';
import PositionedSuggestions from './PositionedSuggestions';
import { useDossier } from '../../dossierEditor/DossierContext';
import { useSparkleAiAnalysis } from '../hooks/useSparkleAiAnalysis';
import { useDragHandle } from '../DragHandleContext';
import Trash from '../../icons/trash';
import Clone from '../../icons/square-2-stack';
import Arrows from '../../icons/Bars3';
import Sparkles from '../../icons/Sparkles';
import Plus from '../../icons/Plus';
import Title from '../../icons/Title';
import Paragraph from '../../icons/Paragraph';
import ListBullet from '../../icons/ListBullet';
import Book from '../../icons/Book';
import Gif from '../../icons/Gif';
import PlayCircle from '../../icons/PlayCircle';
import TableIcon from '../../icons/Window';
import ImageIcon from '../../icons/Image';
import SeparatorIcon from '../../icons/SeparatorIcon';
import Bars3BottomLeft from '../../icons/Bars3BottomLeft';
import Microphone from '../../icons/Microphone';
import { useVoiceDictation } from '../hooks/useVoiceDictation';

interface WidgetRendererProps {
  block: IBlock | ICompositeTextWidget | IImageBlock | IVideoBlock | IParagraphWidget | ISummaryBlock;
  onChange: (newBlock: IBlock | ICompositeTextWidget | IImageBlock | IVideoBlock | IParagraphWidget | ISummaryBlock) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  suggestions: ISuggestion[];
  onAcceptSuggestion: (suggestion: ISuggestion) => void;
  onRejectSuggestion: (suggestion: ISuggestion) => void;
  dossierId: string;
  onSuggestionCreated: (newSuggestion: ISuggestion) => void;
  userRole: string | null;
  isFirstBlock?: boolean;
  chapterIndex: number;
  chapterTitle: string;
  dossierContent?: IDossier | null;
  onInsertAfter?: (widgetType: string) => void;
}

const WidgetRenderer: React.FC<WidgetRendererProps> = ({
  block,
  onChange,
  onDuplicate,
  onDelete,
  suggestions,
  onAcceptSuggestion,
  onRejectSuggestion,
  dossierId,
  onSuggestionCreated,
  userRole,
  isFirstBlock = false,
  chapterIndex,
  chapterTitle,
  dossierContent,
  onInsertAfter, // Destructured here
}) => {
  const { editMode: currentState } = useDossier();
  // State for the insert menu
  const [showInsertMenu, setShowInsertMenu] = useState(false);
  const { listeners, attributes } = useDragHandle();
  const [isHovered, setIsHovered] = useState(false);

  const isEditor = currentState === 'editor';
  const blockRef = useRef<HTMLDivElement>(null);
  const insertMenuRef = useRef<HTMLDivElement>(null);

  // Close insert menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (insertMenuRef.current && !insertMenuRef.current.contains(event.target as Node)) {
        setShowInsertMenu(false);
      }
    };

    if (showInsertMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showInsertMenu]);

  // Intégration du hook d'analyse IA
  const {
    analyzeText,
    applyAnalysisToCompositeBlock,
    isAnalyzing
  } = useSparkleAiAnalysis(dossierId);

  // Dictée vocale pour les blocs compositeText/paragraph
  const { isRecording, isTranscribing, startRecording, stopAndTranscribe, volumeData } = useVoiceDictation();

  const handleMicrophoneClick = useCallback(async () => {
    if (block.type !== 'compositeText' && block.type !== 'paragraph') return;

    if (isRecording) {
      try {
        const text = await stopAndTranscribe();
        if (!text) return;

        const textBlock = block as ICompositeTextWidget | IParagraphWidget;
        const updatedBlocks = [...textBlock.content.blocks];
        const lastBlock = updatedBlocks[updatedBlocks.length - 1];
        const existingText = lastBlock.rawText.replace(/<br\s*\/?>/gi, '').trim();
        const newRawText = existingText ? `${existingText} ${text}` : text;

        updatedBlocks[updatedBlocks.length - 1] = { ...lastBlock, rawText: newRawText };
        onChange({ ...textBlock, content: { ...textBlock.content, blocks: updatedBlocks } } as any);
      } catch (err) {
        console.error('Erreur dictée:', err);
      }
    } else {
      try {
        await startRecording();
      } catch (err) {
        console.error('Erreur démarrage dictée:', err);
      }
    }
  }, [block, isRecording, stopAndTranscribe, startRecording, onChange]);

  // Handler pour l'analyse IA
  const handleSparkleClick = useCallback(async () => {
    if (block.type !== 'compositeText' && block.type !== 'paragraph') return;

    const textBlock = block as ICompositeTextWidget | IParagraphWidget;

    // Extraire le texte de tous les blocs de texte
    const allText = textBlock.content.blocks
      .map(textBlock => textBlock.rawText)
      .join('\n');

    // Lancer l'analyse du texte avec le hook
    const results = await analyzeText(allText);

    if (results) {
      // Appliquer les modifications au bloc
      const updatedBlock = applyAnalysisToCompositeBlock(textBlock, results);

      // Mettre à jour le bloc avec les références enrichies
      onChange(updatedBlock);

    }
  }, [block, analyzeText, applyAnalysisToCompositeBlock, onChange]);

  // Handlers d'événements mémoïsés
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  // Rendu du contenu du bloc
  const renderBlockContent = useMemo(() => {
    switch (block.type) {
      case 'image':
        return (
          <ImageWidget
            block={block as IImageBlock}
            onChange={onChange}
            currentState={currentState}
          />
        );

      case 'video':
        return (
          <VideoWidget
            block={block as IVideoBlock}
            onChange={onChange}
            dossierId={dossierId}
          />
        );

      case 'title':
        const titleBlock = block as ITitleBlock;
        return (
          <TitleWidget
            title={titleBlock.content}
            level={titleBlock.level || 1}
            alignment={titleBlock.alignment || 'left'}
            showInSummary={titleBlock.showInSummary ?? true}
            onTitleChange={(newTitle) =>
              onChange({ ...titleBlock, content: newTitle } as ITitleBlock)
            }
            onLevelChange={(newLevel) =>
              onChange({ ...titleBlock, level: newLevel } as ITitleBlock)
            }
            onAlignmentChange={(newAlignment) =>
              onChange({ ...titleBlock, alignment: newAlignment } as ITitleBlock)
            }
            onToggleSummary={(show) =>
              onChange({ ...titleBlock, showInSummary: show } as ITitleBlock)
            }
          />
        );

      case 'compositeText':
        const compositeTextBlock = block as ICompositeTextWidget;
        return (
          <CompositeTextWidget
            widget={compositeTextBlock}
            onWidgetChange={onChange}
            mode={currentState}
            dossierId={dossierId}
            onSuggestionCreated={onSuggestionCreated}
            mongoId={block._id}
            userRole={userRole}
            chapterIndex={chapterIndex}
            chapterTitle={chapterTitle}
          />
        );

      case 'paragraph':
        const paragraphBlock = block as IParagraphWidget;
        return (
          <ParagraphWidget
            widget={paragraphBlock}
            onWidgetChange={onChange as (newWidget: IParagraphWidget) => void}
            mode={currentState}
            dossierId={dossierId}
            onSuggestionCreated={onSuggestionCreated}
            mongoId={block._id}
            userRole={userRole}
            chapterIndex={chapterIndex}
            chapterTitle={chapterTitle}
          />
        );

      case 'list':
        const listBlock = block as IListBlock;
        return (
          <ListWidget
            block={listBlock}
            onChange={(newBlock) => onChange(newBlock)}
            dossierId={dossierId}
            userRole={userRole}
            chapterIndex={chapterIndex}
            chapterTitle={chapterTitle}
            onSuggestionCreated={onSuggestionCreated}
          />
        );

      case 'gif':
        const gifBlock = block as IGifBlock;
        return <GifWidget block={gifBlock} onChange={onChange} />;

      case 'table':
        const tableBlock = block as ITableBlock;
        return <TableWidget block={tableBlock} onChange={onChange} />;

      case 'summary':
        const summaryBlock = block as ISummaryBlock;
        if (!dossierContent) return <div>Erreur: Contenu du dossier non disponible pour le sommaire</div>;
        return (
          <SummaryWidget
            block={summaryBlock}
            dossierContent={dossierContent}
            currentChapterIndex={chapterIndex}
            onChange={(newBlock) => onChange(newBlock)}
            mode={currentState}
          />
        );

      case 'separator':
        return (
          <SeparatorWidget
            block={block as ISeparatorBlock}
            onChange={(newBlock) => onChange(newBlock)}
            mode={currentState}
          />
        );

      default:
        return <span>Unknown block type: {block.type}</span>;
    }
  }, [block, onChange, currentState, dossierId, onSuggestionCreated, userRole, chapterIndex, chapterTitle, dossierContent]);

  // Vérifications de permissions
  const canViewSuggestions = useMemo(() =>
    userRole === 'author' || userRole === 'admin',
    [userRole]
  );

  // Variables dérivées pour le rendu
  const isCompositeOrParagraph = block.type === 'compositeText' || block.type === 'paragraph';
  const hasSparkleButton = isHovered && isCompositeOrParagraph && isEditor;
  const isCurrentlyAnalyzing = isAnalyzing;

  return (
    <div
      ref={blockRef}
      className={`block block-hover relative ${isEditor ? 'editor-mode-block' : 'reader-mode-block'} ${isFirstBlock ? 'first-block' : ''}`}
      style={{ pointerEvents: 'auto' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-spatial-element="block"
      data-block-id={block._id}
      data-block-type={block.type}
    >
      {/* Contrôles d'édition (uniquement en mode editor) */}
      {isEditor && (
        <div className="block-controls flex items-center pr-2 gap-2 pointer-events-none">
          <div className="icon-container flex items-center pointer-events-auto">
            <div className="icon move-icon" {...listeners} {...attributes} style={{ cursor: 'grab' }}>
              <Arrows />
            </div>
            <div onClick={onDuplicate} className="icon">
              <Clone />
            </div>
            <div onClick={onDelete} className="icon">
              <Trash />
            </div>

            {hasSparkleButton && (
              <div
                onClick={isCurrentlyAnalyzing ? undefined : handleSparkleClick}
                className={`icon ${isCurrentlyAnalyzing ? 'opacity-50 cursor-wait' : 'cursor-pointer'} text-mcdm-secondary-400 hover:text-mcdm-secondary-500 dark:hover:text-mcdm-third-300`}
                title="Analyser et enrichir le texte"
              >
                {isCurrentlyAnalyzing ? (
                  <div className="sparkle-loading">
                    <div className="animate-spin h-5 w-5 border-t-2 border-mcdm-secondary-500 dark:border-mcdm-primary-100 rounded-full"></div>
                  </div>
                ) : (
                  <Sparkles width="19px" height="19px" />
                )}
              </div>
            )}

            {isCompositeOrParagraph && (isHovered || isRecording || isTranscribing) && (
              <div
                onClick={isTranscribing ? undefined : handleMicrophoneClick}
                className={`flex items-center justify-center transition-all duration-300 ease-in-out ${isTranscribing
                  ? 'opacity-50 cursor-wait'
                  : 'cursor-pointer'
                  } ${!isRecording && !isTranscribing ? 'icon' : ''}`}
                title={isRecording ? 'Arrêter la dictée' : isTranscribing ? 'Transcription en cours...' : 'Dicter du texte'}
                style={{ width: '19px', height: '19px' }}
              >
                {isTranscribing ? (
                  <div className="animate-spin h-5 w-5 border-t-2 border-mcdm-secondary-500 dark:border-mcdm-primary-100 rounded-full"></div>
                ) : (
                  <>
                    <div className={`transition-all duration-300 ease-in-out ${isRecording ? 'w-3.5 h-3.5 bg-red-500 rounded-full shadow-[0_0_6px_rgba(239,68,68,0.6)] animate-pulse' : 'w-0 h-0 opacity-0'}`} />
                    {!isRecording && (
                      <div className="transition-all duration-300 ease-in-out flex items-center justify-center">
                        <Microphone width="19" height="19" />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Indicateur de dictée vocale sur le côté droit immédiat */}
          {isCompositeOrParagraph && (isRecording || isTranscribing) && (
            <div className="flex items-center gap-3 bg-white/90 dark:bg-mcdm-third-400/90 px-3 py-1.5 rounded-md shadow-sm border border-gray-100 dark:border-mcdm-third-500 transition-all duration-300 pointer-events-auto">
              <div className="text-xs text-mcdm-secondary-500 dark:text-mcdm-third-500 font-medium whitespace-nowrap">
                {isTranscribing ? 'Transcription...' : 'Écoute en cours'}
              </div>

              {isRecording && (
                <div className="flex items-center gap-[3px] h-3.5">
                  <div className="w-[3px] bg-red-500 rounded-full transition-all duration-75" style={{ height: `${volumeData?.[0] || 15}%` }}></div>
                  <div className="w-[3px] bg-red-500 rounded-full transition-all duration-75" style={{ height: `${volumeData?.[1] || 15}%` }}></div>
                  <div className="w-[3px] bg-red-500 rounded-full transition-all duration-75" style={{ height: `${volumeData?.[2] || 15}%` }}></div>
                  <div className="w-[3px] bg-red-500 rounded-full transition-all duration-75" style={{ height: `${volumeData?.[3] || 15}%` }}></div>
                  <div className="w-[3px] bg-red-500 rounded-full transition-all duration-75" style={{ height: `${volumeData?.[4] || 15}%` }}></div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Contenu du bloc */}
      {renderBlockContent}

      {/* Bouton d'ajout rapide (Bottom Left) */}
      {isEditor && onInsertAfter && (
        <div
          ref={insertMenuRef}
          className={`absolute -bottom-3 left-0 z-20 transition-opacity duration-200 ${isHovered || showInsertMenu ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
        >
          <div className="relative">
            <div
              onClick={(e) => {
                e.stopPropagation();
                setShowInsertMenu(!showInsertMenu);
              }}
              className={`flex items-center justify-center w-[20px] h-[20px] rounded bg-white dark:bg-mcdm-third-400 border border-[#b7b7b7] dark:border-[#3f3f3f] shadow-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-mcdm-third-300 text-gray-500 dark:text-mcdm-primary-100 transition-colors ${showInsertMenu ? 'bg-gray-100 dark:bg-mcdm-third-300 text-mcdm-secondary-500' : ''
                }`}
              title="Insérer un bloc en dessous"
            >
              <Plus width="12" height="12" />
            </div>

            {showInsertMenu && (
              <div
                className="absolute top-full left-0 mt-2 bg-white dark:bg-mcdm-third-400 border border-gray-200 dark:border-mcdm-third-500 rounded-lg shadow-xl z-50 py-2 w-48 cursor-auto max-h-64 overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col">
                  {[
                    { type: 'title', label: 'Titre', icon: Title },
                    { type: 'paragraph', label: 'Paragraphe', icon: Paragraph },
                    { type: 'CompositeTextWidget', label: 'Texte', icon: Bars3BottomLeft },
                    { type: 'separator', label: 'Séparateur', icon: SeparatorIcon },
                    { type: 'list', label: 'Liste', icon: ListBullet },
                    { type: 'image', label: 'Image', icon: ImageIcon },
                    { type: 'summary', label: 'Sommaire', icon: Book },
                    { type: 'table', label: 'Tableau', icon: TableIcon },
                    { type: 'gif', label: 'GIF', icon: Gif },
                    { type: 'video', label: 'Vidéo', icon: PlayCircle },
                  ].map((widget) => (
                    <div
                      key={widget.type}
                      className="flex items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-mcdm-third-300 cursor-pointer transition-colors"
                      onClick={() => {
                        onInsertAfter(widget.type);
                        setShowInsertMenu(false);
                      }}
                    >
                      <div className="text-mcdm-third-500 dark:text-mcdm-primary-100 mr-3">
                        <widget.icon width="16" height="16" />
                      </div>
                      <span className="text-sm text-gray-700 dark:text-gray-200 font-medium">
                        {widget.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Suggestions (uniquement pour author/admin) */}
      {canViewSuggestions && suggestions.length > 0 && (
        <PositionedSuggestions
          suggestions={suggestions}
          blockElement={blockRef.current}
          onAccept={onAcceptSuggestion}
          onReject={onRejectSuggestion}
        />
      )}
    </div>
  );
};

// Mémoïsation du composant avec comparaison personnalisée
export default React.memo(WidgetRenderer, (prevProps, nextProps) => {
  // Comparaison des props simples
  if (
    prevProps.dossierId !== nextProps.dossierId ||
    prevProps.userRole !== nextProps.userRole ||
    prevProps.chapterIndex !== nextProps.chapterIndex ||
    prevProps.chapterTitle !== nextProps.chapterTitle ||
    prevProps.onDuplicate !== nextProps.onDuplicate ||
    prevProps.onDelete !== nextProps.onDelete ||
    prevProps.onChange !== nextProps.onChange ||
    prevProps.onAcceptSuggestion !== nextProps.onAcceptSuggestion ||
    prevProps.onRejectSuggestion !== nextProps.onRejectSuggestion ||
    prevProps.onSuggestionCreated !== nextProps.onSuggestionCreated ||
    prevProps.isFirstBlock !== nextProps.isFirstBlock
  ) {
    return false;
  }

  // Comparaison approfondie du bloc
  if (JSON.stringify(prevProps.block) !== JSON.stringify(nextProps.block)) {
    return false;
  }

  // Comparaison des suggestions
  if (prevProps.suggestions.length !== nextProps.suggestions.length) {
    return false;
  }

  // Comparer les IDs des suggestions
  for (let i = 0; i < prevProps.suggestions.length; i++) {
    if (prevProps.suggestions[i]._id !== nextProps.suggestions[i]._id) {
      return false;
    }
  }

  // Pour le bloc de sommaire, vérifier si le contenu du dossier a changé
  if (prevProps.block.type === 'summary' && prevProps.dossierContent !== nextProps.dossierContent) {
    return false;
  }

  return true;
});